import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Header, Footer } from "../components/layout";
import { AppDownloadBanner } from "../components/sections/AppDownloadBanner";
import { SEO } from "../components/SEO";
import { Container } from "../components/ui/Container";
import { fetchShopsDirectory, ShopDirectoryItem } from "../apis/shops";
import { fetchExploreCategories, ExploreCategoryItem } from "../apis/categories";
import { getSmartAppDownloadUrl } from "../utils/appLinks";
import {
  CITY_COORDINATES,
  POPULAR_LOCATIONS,
  DEFAULT_COORDINATES,
} from "../utils/locations";

export interface SidebarCategory {
  id: string;
  name: string;
  slug: string;
  iconType?: string;
  iconUrl?: string | null;
  subcategories?: string[];
  matcher?: (item: ShopDirectoryItem) => boolean;
}

const DEFAULT_SIDEBAR_CATEGORIES: SidebarCategory[] = [
  {
    id: "all",
    name: "All categories",
    slug: "all",
    matcher: () => true,
  },
  {
    id: "restaurants",
    name: "Restaurants & Cafés",
    slug: "restaurants-cafes",
    subcategories: ["Café", "Bakery", "Fine Dining", "Fast Food", "Buffet", "Catering"],
    matcher: (item) =>
      /restaurant|cafe|bakery|food|dining|catering|biryani/i.test(
        `${item.category} ${(item.serviceCategories || []).join(" ")} ${item.name}`
      ),
  },
  {
    id: "beauty",
    name: "Beauty & Wellness",
    slug: "beauty-wellness",
    subcategories: ["Salon & Spa", "Hair Styling", "Grooming", "Skin Care", "Nails & Makeup"],
    matcher: (item) =>
      /beauty|salon|spa|wellness|grooming|hair/i.test(
        `${item.category} ${(item.serviceCategories || []).join(" ")} ${item.name}`
      ),
  },
  {
    id: "grocery",
    name: "Grocery & Retail",
    slug: "grocery-retail",
    subcategories: ["Supermarket", "Organic Stores", "Provisions", "Convenience Store"],
    matcher: (item) =>
      /grocery|retail|supermarket|organic|store|provisions/i.test(
        `${item.category} ${(item.serviceCategories || []).join(" ")} ${item.name}`
      ),
  },
  {
    id: "fashion",
    name: "Fashion & Lifestyle",
    slug: "fashion-lifestyle",
    subcategories: ["Clothing", "Footwear", "Textiles", "Accessories", "Jewellery"],
    matcher: (item) =>
      /fashion|lifestyle|clothing|apparel|textile|jewel|watch|saree/i.test(
        `${item.category} ${(item.serviceCategories || []).join(" ")} ${item.name}`
      ),
  },
  {
    id: "fitness",
    name: "Fitness & Sports",
    slug: "fitness-sports",
    subcategories: ["Gym", "Fitness Club", "Yoga", "Turf", "Sports Gear"],
    matcher: (item) =>
      /fitness|sports|gym|turf|yoga/i.test(
        `${item.category} ${(item.serviceCategories || []).join(" ")} ${item.name}`
      ),
  },
  {
    id: "auto",
    name: "Automotive Services",
    slug: "automotive-services",
    subcategories: ["Car Wash", "Car Detailing", "Auto Repair", "Tyres & Wheels", "Bike Service"],
    matcher: (item) =>
      /auto|car|bike|vehicle|mechanic|wash|tyre|tire/i.test(
        `${item.category} ${(item.serviceCategories || []).join(" ")} ${item.name}`
      ),
  },
  {
    id: "home",
    name: "Home Services",
    slug: "home-services",
    subcategories: ["Home Cleaning", "Appliance Repair", "Electrical", "Plumbing", "Interior Design", "Pest Control"],
    matcher: (item) =>
      /home|clean|repair|electr|plumb|pest|construction|architect|decor|furniture/i.test(
        `${item.category} ${(item.serviceCategories || []).join(" ")} ${item.name}`
      ),
  },
];


export default function AllShopsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialCategory = searchParams.get("category") || "all";
  const initialSearch = searchParams.get("search") || "";
  const initialLocation = searchParams.get("location") || "All";

  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(
    CITY_COORDINATES[initialLocation] || DEFAULT_COORDINATES
  );
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);

  const [allShops, setAllShops] = useState<ShopDirectoryItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalShops, setTotalShops] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [backendCategories, setBackendCategories] = useState<ExploreCategoryItem[]>([]);
  const [categorySearch, setCategorySearch] = useState("");

  const locationDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch dynamic categories from backend API
  useEffect(() => {
    let cancelled = false;
    fetchExploreCategories().then((cats) => {
      if (!cancelled && Array.isArray(cats) && cats.length > 0) {
        setBackendCategories(cats.filter((c) => c.id !== "all" && c.slug !== "all"));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Background non-blocking GPS detection
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        if (selectedLocation === "All" || selectedLocation === "Current Location") {
          setUserCoords(coords);
        }
      },
      () => {},
      { maximumAge: 600_000, timeout: 10_000 }
    );
  }, []);

  const handleDetectGPS = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setSelectedLocation("Current Location");
        setUserCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setIsLocationDropdownOpen(false);
      },
      () => {
        alert("Location access denied or unavailable.");
      }
    );
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLocationDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync to URL
  useEffect(() => {
    const params: Record<string, string> = {};
    if (selectedCategory && selectedCategory !== "all") {
      params.category = selectedCategory;
    }
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }
    if (selectedLocation && selectedLocation !== "All") {
      params.location = selectedLocation;
    }
    setSearchParams(params, { replace: true });
  }, [selectedCategory, searchQuery, selectedLocation, setSearchParams]);

  // ── Unified Sidebar Categories (Backend API + Live Shops + Defaults) ─────────
  const allSidebarCategories = useMemo<SidebarCategory[]>(() => {
    const result: SidebarCategory[] = [
      {
        id: "all",
        name: "All categories",
        slug: "all",
        matcher: () => true,
      },
    ];

    const seenSlugs = new Set<string>(["all"]);
    const seenNames = new Set<string>(["all categories"]);

    const addCat = (cat: SidebarCategory) => {
      const normSlug = cat.slug.toLowerCase().trim();
      const normName = cat.name.toLowerCase().trim();
      if (!seenSlugs.has(normSlug) && !seenNames.has(normName)) {
        seenSlugs.add(normSlug);
        seenNames.add(normName);
        result.push(cat);
      }
    };

    // 1. Dynamic categories from backend API
    backendCategories.forEach((bc) => {
      if (bc.id === "all" || bc.slug === "all") return;
      addCat({
        id: bc.id,
        name: bc.name,
        slug: bc.slug || bc.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
        iconType: bc.iconType,
        iconUrl: bc.iconUrl,
        subcategories: Array.isArray(bc.subcategories) ? bc.subcategories : [],
      });
    });

    // 2. Distinct categories from loaded shops
    allShops.forEach((shop) => {
      if (shop.category && shop.category.trim()) {
        const catName = shop.category.trim();
        const catSlug = catName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
        addCat({
          id: catSlug,
          name: catName,
          slug: catSlug,
        });
      }
    });

    // 3. Fallback / friendly curated categories
    DEFAULT_SIDEBAR_CATEGORIES.forEach((df) => {
      if (df.id !== "all") {
        addCat(df);
      }
    });

    return result;
  }, [backendCategories, allShops]);

  // Check if category matches currently selected category
  const isCategoryActive = (cat: SidebarCategory): boolean => {
    if (cat.id === "all" || cat.slug === "all") {
      return selectedCategory === "all" || !selectedCategory;
    }
    if (selectedCategory === cat.id || selectedCategory === cat.slug) {
      return true;
    }

    const normSelected = selectedCategory.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-");
    const normCatSlug = cat.slug.toLowerCase().trim();
    const normCatName = cat.name.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-");

    if (normSelected === normCatSlug || normSelected === normCatName) {
      return true;
    }

    // Legacy / friendly shortcuts
    if (selectedCategory === "home" && (normCatSlug.includes("home") || cat.id === "home")) return true;
    if (selectedCategory === "restaurants" && (normCatSlug.includes("restaurant") || cat.id === "restaurants")) return true;
    if (selectedCategory === "beauty" && (normCatSlug.includes("beauty") || normCatSlug.includes("salon") || cat.id === "beauty")) return true;
    if (selectedCategory === "grocery" && (normCatSlug.includes("grocery") || normCatSlug.includes("retail") || cat.id === "grocery")) return true;
    if (selectedCategory === "fashion" && (normCatSlug.includes("fashion") || normCatSlug.includes("clothing") || cat.id === "fashion")) return true;
    if (selectedCategory === "fitness" && (normCatSlug.includes("fitness") || normCatSlug.includes("sports") || cat.id === "fitness")) return true;
    if (selectedCategory === "auto" && (normCatSlug.includes("auto") || normCatSlug.includes("car") || cat.id === "auto")) return true;

    return false;
  };

  const activeCategoryObj = useMemo(() => {
    if (!selectedCategory || selectedCategory === "all") {
      return { id: "all", name: "All categories", slug: "all" };
    }
    const found = allSidebarCategories.find(isCategoryActive);
    if (found) return found;

    const norm = selectedCategory.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const foundNorm = allSidebarCategories.find(
      (c) => c.slug === norm || c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") === norm
    );
    if (foundNorm) return foundNorm;

    return { id: selectedCategory, name: selectedCategory, slug: selectedCategory };
  }, [selectedCategory, allSidebarCategories]);

  // Debounced search query for API calls
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch shops from live API (page 1 on filter, location, or search change)
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const isAll =
      !selectedLocation ||
      selectedLocation === "All" ||
      selectedLocation.toLowerCase() === "all locations";

    const categoryParam =
      selectedCategory !== "all" ? activeCategoryObj.name || selectedCategory : undefined;

    fetchShopsDirectory({
      page: 1,
      limit: 12,
      category: categoryParam,
      search: debouncedSearchQuery.trim() || undefined,
      location: isAll ? undefined : selectedLocation,
      lat: isAll ? undefined : userCoords?.lat,
      lng: isAll ? undefined : userCoords?.lng,
      sort: isAll ? "newest" : "nearest",
    })
      .then((data) => {
        if (!cancelled) {
          setAllShops(data.items || []);
          setCurrentPage(1);
          setTotalShops(data.pagination?.total ?? (data.items || []).length);
          setTotalPages(data.pagination?.pages ?? 1);
        }
      })
      .catch((err) => {
        console.warn("fetchShopsDirectory failed:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedLocation,
    selectedCategory,
    debouncedSearchQuery,
    userCoords,
    activeCategoryObj.name,
  ]);

  // Handle server-side "Load more"
  const handleLoadMore = async () => {
    if (loadingMore || currentPage >= totalPages) return;
    setLoadingMore(true);

    const nextPage = currentPage + 1;
    const isAll =
      !selectedLocation ||
      selectedLocation === "All" ||
      selectedLocation.toLowerCase() === "all locations";

    const categoryParam =
      selectedCategory !== "all" ? activeCategoryObj.name || selectedCategory : undefined;

    try {
      const data = await fetchShopsDirectory({
        page: nextPage,
        limit: 12,
        category: categoryParam,
        search: debouncedSearchQuery.trim() || undefined,
        location: isAll ? undefined : selectedLocation,
        lat: isAll ? undefined : userCoords?.lat,
        lng: isAll ? undefined : userCoords?.lng,
        sort: isAll ? "newest" : "nearest",
      });

      if (data?.items?.length) {
        setAllShops((prev) => {
          const seen = new Set(prev.map((s) => s.id));
          return [...prev, ...data.items.filter((s) => !seen.has(s.id))];
        });
        setCurrentPage(nextPage);
        if (data.pagination) {
          setTotalPages(data.pagination.pages || totalPages);
          setTotalShops(data.pagination.total || totalShops);
        }
      }
    } catch (err) {
      console.warn("handleLoadMore failed:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  // Categories sorted for display (alphabetically, with "All categories" first)
  const displayedSidebarCategories = useMemo(() => {
    const allCat = allSidebarCategories.find((c) => c.id === "all");
    const others = allSidebarCategories.filter((c) => c.id !== "all");

    let list = others;
    if (categorySearch.trim()) {
      const q = categorySearch.toLowerCase().trim();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.slug.toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => a.name.localeCompare(b.name));

    return allCat ? [allCat, ...list] : list;
  }, [allSidebarCategories, categorySearch]);

  const displayedShops = allShops;

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 flex flex-col font-sans selection:bg-purple-100 selection:text-purple-900">
      <SEO
        title={
          selectedLocation === "All"
            ? "All Partner Shops across Kerala | SetGo"
            : selectedLocation === "Current Location"
            ? "Partner Shops Near You | SetGo"
            : `All shops near ${selectedLocation} | SetGo`
        }
        description={`Explore verified partner shops, restaurants, salons and services ${
          selectedLocation === "All"
            ? "across Kerala"
            : `near ${selectedLocation}`
        }. Earn SetGo loyalty coins on every purchase.`}
        url="/shops"
      />

      <Header variant="solid" />

      <main className="flex-1 pt-24 pb-16">
        <Container size="xl" padding="md">
          {/* ── BREADCRUMB ───────────────────────────────────────────────────────── */}
          <nav aria-label="Breadcrumb" className="mb-4">
            <ol className="flex items-center gap-2 text-xs sm:text-sm text-slate-500">
              <li>
                <Link to="/deals" className="hover:text-slate-900 transition-colors">
                  Deals near you
                </Link>
              </li>
              <li className="text-slate-400">/</li>
              <li className="font-bold text-slate-900">All shops</li>
            </ol>
          </nav>

          {/* ── HEADER ROW: TITLE & AREA SELECTOR ─────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-slate-950 tracking-tight leading-tight">
                {selectedLocation === "All"
                  ? "All shops"
                  : selectedLocation === "Current Location"
                  ? "All shops near you"
                  : `All shops near ${selectedLocation}`}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <p className="text-xs sm:text-sm text-slate-500">
                  {loading ? "Loading..." : `${totalShops} ${totalShops === 1 ? "shop" : "shops"} · `}
                  {activeCategoryObj.id === "all" ? "all categories" : activeCategoryObj.name}
                </p>
              </div>
            </div>

            {/* Area Selector Dropdown */}
            <div className="relative self-start sm:self-auto" ref={locationDropdownRef}>
              <div className="text-[11px] font-medium text-slate-400 mb-1">Area</div>
              <button
                type="button"
                onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm font-semibold text-slate-900 hover:border-slate-300 shadow-xs transition-colors"
              >
                <span>{selectedLocation === "All" ? "All Locations" : selectedLocation}</span>
                <svg
                  className={`w-4 h-4 text-slate-500 transition-transform ${
                    isLocationDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isLocationDropdownOpen && (
                <div className="absolute right-0 mt-1 w-64 bg-white rounded-2xl shadow-xl border border-slate-100 py-1.5 z-40 max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-purple-200">
                  {/* GPS Option */}
                  <button
                    type="button"
                    onClick={handleDetectGPS}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-xs font-bold text-purple-700 bg-purple-50/70 hover:bg-purple-100 transition-colors border-b border-slate-100 text-left"
                  >
                    <svg className="w-4 h-4 flex-shrink-0 animate-pulse text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      <circle cx="12" cy="12" r="4" fill="currentColor" />
                    </svg>
                    <span>Use Current GPS Location</span>
                  </button>

                  {/* "All" Option */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedLocation("All");
                      setIsLocationDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-xs sm:text-sm flex items-center justify-between transition-colors border-b border-slate-100 ${
                      selectedLocation === "All"
                        ? "bg-purple-50 text-[#552FB5] font-bold"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <span>All Locations (All Kerala)</span>
                    {selectedLocation === "All" && (
                      <svg className="w-4 h-4 text-[#552FB5]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>

                  <div className="px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Districts (Kerala)
                  </div>

                  {POPULAR_LOCATIONS.map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => {
                        setSelectedLocation(loc);
                        if (CITY_COORDINATES[loc]) {
                          setUserCoords(CITY_COORDINATES[loc]);
                        }
                        setIsLocationDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-xs sm:text-sm flex items-center justify-between transition-colors ${
                        selectedLocation === loc
                          ? "bg-purple-50 text-[#552FB5] font-bold"
                          : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span>{loc}</span>
                      {selectedLocation === loc && (
                        <svg className="w-4 h-4 text-[#552FB5]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── 2-COLUMN LAYOUT: SIDEBAR + SHOPS GRID ──────────────────────────────── */}
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            {/* ── LEFT SIDEBAR (Sticky on Desktop) ─────────────────────────────────── */}
            <aside className="w-full lg:w-72 flex-shrink-0 lg:sticky lg:top-24 lg:self-start lg:h-fit z-10">
              <div className="glass-light rounded-3xl p-4 sm:p-5 space-y-4 lg:max-h-[calc(100vh-7rem)] flex flex-col">
                {/* Search Input */}
                <div className="relative flex-shrink-0">
                  <svg
                    className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    placeholder="Search shops"
                    className="w-full bg-[#F1F3F9] border-0 rounded-xl pl-9 pr-3.5 py-2.5 text-xs sm:text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#552FB5] transition-all"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Categories Header & List */}
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex items-center justify-between mb-2 flex-shrink-0">
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                      CATEGORY
                    </span>
                    <span className="text-[10px] font-semibold text-slate-400">
                      {allSidebarCategories.length - 1} categories
                    </span>
                  </div>

                  {/* Category Filter Search */}
                  {allSidebarCategories.length > 8 && (
                    <div className="relative mb-2 flex-shrink-0">
                      <input
                        type="text"
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        placeholder="Filter categories..."
                        className="w-full bg-[#F1F3F9] border-0 rounded-xl px-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#552FB5] transition-all"
                      />
                      {categorySearch && (
                        <button
                          type="button"
                          onClick={() => setCategorySearch("")}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                          aria-label="Clear category filter"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )}

                  {/* Horizontal chip scroller on mobile, vertical list on desktop */}
                  <div className="no-scrollbar flex gap-2 overflow-x-auto -mx-1 px-1 pb-1 lg:block lg:flex-1 lg:min-h-0 lg:overflow-x-visible lg:overflow-y-auto lg:mx-0 lg:px-0 lg:pb-0 lg:pr-1 lg:space-y-1">
                    {displayedSidebarCategories.map((cat) => {
                      const isActive = isCategoryActive(cat);

                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            setSelectedCategory(cat.slug || cat.id);
                          }}
                          className={`flex-shrink-0 whitespace-nowrap lg:w-full flex items-center px-3.5 lg:px-3 py-2 rounded-full lg:rounded-xl border lg:border-0 text-xs sm:text-sm transition-all text-left ${
                            isActive
                              ? "bg-[#EDE9FE] border-purple-200 text-[#552FB5] font-bold shadow-xs"
                              : "bg-slate-50 lg:bg-transparent border-slate-200 text-slate-600 hover:bg-slate-50 font-medium"
                          }`}
                        >
                          <span className="truncate">{cat.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Promo Card: "Take SetGo with you" */}
                <div className="hidden lg:block rounded-2xl p-4 bg-[#F8F9FE] border border-purple-100/80 flex-shrink-0">
                  <h4 className="font-extrabold text-xs sm:text-sm text-slate-900 mb-1">
                    Take SetGo with you
                  </h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed mb-3">
                    Book services, redeem offers and earn coins in the SetGo app.
                  </p>
                  <a
                    href={getSmartAppDownloadUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-2 rounded-xl bg-[#552FB5] hover:bg-[#432096] text-white text-center font-bold text-xs shadow-xs transition-colors"
                  >
                    Get the app
                  </a>
                </div>
              </div>
            </aside>

            {/* ── RIGHT SHOPS GRID ────────────────────────────────────────────────── */}
            <section className="flex-1 min-w-0 w-full">
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <div
                      key={n}
                      className="h-72 rounded-2xl bg-slate-200/60 animate-pulse border border-slate-100"
                    />
                  ))}
                </div>
              ) : allShops.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-slate-200 p-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-50 text-[#552FB5] flex items-center justify-center text-2xl font-bold">
                    🏪
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    No shops found {selectedLocation !== "All" ? `near ${selectedLocation}` : ""}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 mb-5 max-w-md mx-auto">
                    {selectedLocation !== "All"
                      ? `We couldn't find any partner shops in ${selectedLocation} yet. Try exploring all partner shops across Kerala sorted by distance.`
                      : `We couldn't find any partner shops matching "${searchQuery || activeCategoryObj.name}". Try choosing another category or clearing your filter.`}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedLocation("All");
                      setSelectedCategory("all");
                      setSearchQuery("");
                    }}
                    className="px-5 py-2.5 rounded-full bg-[#552FB5] text-white text-xs font-bold hover:bg-[#432096] transition-colors shadow-xs inline-flex items-center gap-1.5"
                  >
                    <span>View All Shops (Distance sorted)</span>
                    <span>→</span>
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {displayedShops.map((shop) => {
                      const rawOffers = shop.offerCount || 0;
                      const hasOffers = rawOffers > 0;
                      const ratingVal =
                        typeof shop.rating === "number" && shop.rating > 0
                          ? shop.rating.toFixed(1)
                          : null;

                      const shopImg =
                        shop.coverImage ||
                        shop.image ||
                        shop.logo ||
                        "/deals/shop_vibe.jpg";

                      const initials = (shop.name || "S")
                        .split(" ")
                        .slice(0, 2)
                        .map((w) => w[0])
                        .join("")
                        .toUpperCase();

                      return (
                        <Link
                          key={shop.id || shop.slug}
                          to={`/shops/${shop.slug}`}
                          className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between"
                        >
                          {/* Image with Offer Count Pill on Top Right */}
                          <div className="relative aspect-[16/10] w-full overflow-hidden bg-slate-100">
                            <img
                              src={shopImg}
                              alt={shop.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                            {hasOffers && (
                              <div className="absolute top-2.5 right-2.5">
                                <span className="inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-white/95 backdrop-blur-sm text-emerald-800 border border-emerald-100/60 shadow-xs">
                                  {rawOffers} {rawOffers === 1 ? "offer" : "offers"}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Card Body: Logo on left, details on right */}
                          <div className="p-4 flex items-center gap-3">
                            <div className="w-11 h-11 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center p-1 flex-shrink-0 shadow-xs overflow-hidden">
                              {shop.logo ? (
                                <img
                                  src={shop.logo}
                                  alt={shop.name}
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              ) : (
                                <span className="text-xs font-bold text-slate-700">
                                  {initials}
                                </span>
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <h3 className="font-bold text-sm sm:text-base text-slate-900 group-hover:text-[#552FB5] transition-colors truncate">
                                {shop.name}
                              </h3>
                              <p className="text-xs text-slate-500 truncate">
                                {shop.category || "Retail Store"}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-600">
                                {ratingVal && (
                                  <span className="flex items-center gap-0.5 font-bold text-slate-900">
                                    <span className="text-amber-500">★</span>
                                    <span>{ratingVal}</span>
                                  </span>
                                )}
                                {shop.distance && (
                                  <span className="text-slate-500">
                                    {ratingVal ? "· " : ""}
                                    {shop.distance}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>

                  {/* Load More Button & Count */}
                  <div className="mt-8 text-center space-y-2">
                    {currentPage < totalPages && (
                      <button
                        type="button"
                        disabled={loadingMore}
                        onClick={handleLoadMore}
                        className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-white border border-purple-200 text-[#552FB5] font-bold text-xs sm:text-sm hover:bg-purple-50 shadow-xs transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {loadingMore && (
                          <svg
                            className="animate-spin -ml-1 mr-1 h-4 w-4 text-[#552FB5]"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            />
                          </svg>
                        )}
                        <span>{loadingMore ? "Loading more shops..." : "Load more shops"}</span>
                      </button>
                    )}
                    <p className="text-xs text-slate-400">
                      Showing {displayedShops.length} of {totalShops}
                    </p>
                  </div>
                </>
              )}
            </section>
          </div>
        </Container>
      </main>

      {/* ── APP DOWNLOAD BANNER & FOOTER ─────────────────────────────────────────── */}
      <AppDownloadBanner />
      <Footer />
    </div>
  );
}
